// Research Engine — primitive components (ported from claude.ai/design handoff).
// All use design tokens — no raw hex.
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { Icon, type IconName } from "./icon";

export const cn = clsx;

/* ─────────── Button ─────────── */
type ButtonVariant =
  | "primary" | "accent" | "secondary" | "ghost" | "soft"
  | "danger" | "danger-ghost" | "outline" | "link";
type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
}

const BTN_SIZES: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-[12px] gap-1.5 rounded-md",
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-md",
  md: "h-9 px-3.5 text-[13.5px] gap-2 rounded-md",
  lg: "h-11 px-5 text-[14.5px] gap-2 rounded-lg",
  xl: "h-12 px-6 text-[15px] gap-2.5 rounded-lg",
};
const BTN_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-ink text-bg hover:bg-ink/90 ring-focus",
  accent: "bg-accent text-white hover:brightness-110 ring-focus",
  secondary: "bg-surface border border-border text-ink hover:bg-surface-2 hover:border-border-strong ring-focus",
  ghost: "text-ink-dim hover:text-ink hover:bg-surface-2 ring-focus",
  soft: "bg-surface-2 text-ink hover:bg-surface-3 ring-focus",
  danger: "bg-danger text-white hover:brightness-110 ring-focus",
  "danger-ghost": "text-danger hover:bg-danger-soft ring-focus",
  outline: "border border-border-strong text-ink hover:bg-surface-2 ring-focus",
  link: "text-accent-ink underline decoration-from-font underline-offset-4 hover:text-accent",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", icon, iconRight, children, className = "", ...props },
  ref,
) {
  const gly = size === "xs" ? 13 : size === "xl" ? 17 : 15;
  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center font-medium transition-colors select-none disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap",
        BTN_SIZES[size],
        BTN_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {icon && <Icon name={icon} size={gly} />}
      {children}
      {iconRight && <Icon name={iconRight} size={gly} />}
    </button>
  );
});

/* ─────────── Badge ─────────── */
export type BadgeTone = "neutral" | "line" | "accent" | "ok" | "warn" | "danger" | "solid";
type BadgeSize = "xs" | "sm" | "md";

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "bg-surface-2 text-ink-dim border-border",
  line: "border-border-strong text-ink-dim",
  accent: "bg-accent-soft text-accent-ink border-transparent",
  ok: "bg-ok-soft text-[color:var(--ok)] border-transparent",
  warn: "bg-warn-soft text-[color:var(--warn)] border-transparent",
  danger: "bg-danger-soft text-[color:var(--danger)] border-transparent",
  solid: "bg-ink text-bg border-transparent",
};
const BADGE_SIZES: Record<BadgeSize, string> = {
  xs: "h-5 px-1.5 text-[10.5px] gap-1",
  sm: "h-6 px-2 text-[11.5px] gap-1.5",
  md: "h-7 px-2.5 text-[12px] gap-1.5",
};

export function Badge({
  tone = "neutral", size = "sm", icon, children, className = "",
}: {
  tone?: BadgeTone;
  size?: BadgeSize;
  icon?: IconName;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-full border tracking-tight uppercase",
        BADGE_TONES[tone],
        BADGE_SIZES[size],
        className,
      )}
      style={{ letterSpacing: "0.04em" }}
    >
      {icon && <Icon name={icon} size={11} />}
      {children}
    </span>
  );
}

/* ─────────── Input ─────────── */
type InputSize = "sm" | "md" | "lg" | "xl";
const INPUT_SIZES: Record<InputSize, string> = {
  sm: "h-8 text-[13px] px-3 rounded-md",
  md: "h-10 text-[14px] px-3.5 rounded-md",
  lg: "h-12 text-[15px] px-4 rounded-lg",
  xl: "h-14 text-[16px] px-5 rounded-lg",
};

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  icon?: IconName;
  suffix?: ReactNode;
  size?: InputSize;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, suffix, size = "md", className = "", containerClassName = "", ...props },
  ref,
) {
  return (
    <div className={cn("relative flex items-center", containerClassName)}>
      {icon && <Icon name={icon} size={16} className="absolute left-3.5 text-ink-faint pointer-events-none" />}
      <input
        ref={ref}
        className={cn(
          "w-full bg-surface border border-border text-ink placeholder:text-ink-faint transition-colors",
          "focus:outline-none focus:border-ink/40 focus:ring-2 focus:ring-accent-soft",
          icon && "pl-10",
          suffix && "pr-12",
          INPUT_SIZES[size],
          className,
        )}
        {...props}
      />
      {suffix && <div className="absolute right-3 text-ink-faint text-[12px]">{suffix}</div>}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = "", ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          "w-full bg-surface border border-border text-ink placeholder:text-ink-faint transition-colors p-3.5 rounded-md",
          "focus:outline-none focus:border-ink/40 focus:ring-2 focus:ring-accent-soft text-[14px] leading-6 font-sans resize-none",
          className,
        )}
        {...props}
      />
    );
  },
);

/* ─────────── Card ─────────── */
export function Card({
  children, className = "", interactive = false, as: As = "div", ...props
}: {
  children?: ReactNode;
  className?: string;
  interactive?: boolean;
  as?: React.ElementType;
} & Record<string, unknown>) {
  return (
    <As
      className={cn(
        "bg-surface border border-border rounded-lg",
        interactive && "transition-colors hover:border-border-strong cursor-pointer",
        className,
      )}
      {...props}
    >
      {children}
    </As>
  );
}

/* ─────────── Kbd ─────────── */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.4em] h-5 px-1 rounded border border-border bg-surface-2 text-ink-dim font-mono text-[10.5px] font-medium">
      {children}
    </kbd>
  );
}

/* ─────────── Avatar (initial-based) ─────────── */
export function Avatar({ name, size = 32, className = "" }: { name?: string; size?: number; className?: string }) {
  const initials = (name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const hue = ((name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) * 23) % 360;
  return (
    <div
      className={cn("inline-flex items-center justify-center rounded-full font-medium select-none", className)}
      style={{
        width: size,
        height: size,
        background: `oklch(94% 0.025 ${hue})`,
        color: `oklch(35% 0.10 ${hue})`,
        fontSize: Math.round(size * 0.36),
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </div>
  );
}

/* ─────────── Live status dot ─────────── */
export function LiveDot({ color = "var(--ok)" }: { color?: string }) {
  return (
    <span className="relative inline-flex w-2 h-2">
      <span className="absolute inset-0 rounded-full live-dot" style={{ background: color }} />
    </span>
  );
}

/* ─────────── Section heading ─────────── */
export function SectionHeading({
  eyebrow, title, action,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-6 mb-5">
      <div>
        {eyebrow && (
          <div className="text-micro font-medium text-ink-faint uppercase mb-2" style={{ letterSpacing: "0.08em" }}>
            {eyebrow}
          </div>
        )}
        <h2 className="font-serif text-[28px] leading-[1.15] font-medium text-ink tracking-tight">{title}</h2>
      </div>
      {action}
    </div>
  );
}
